// Hand-written V2 guest contract (FOYR-563), kept apart from
// `src/api/generated/**`, which the openapi sync regenerates. Every field the
// backend has not shipped yet is optional here and is parsed defensively by
// the guards below: FOYR-564 adds `menus` / `items` / `branding` to the
// restaurant payload, FOYR-565 serves item modifiers, FOYR-566 adds
// `phoneLast4` and `order.placedAt` to the reservation.

/** One entry of the restaurant payload's `menus[]` (FOYR-564). */
export interface V2MenuCategory {
  readonly _id: string;
  readonly name: string;
  /** Menu item `_id`s, in display order. */
  readonly itemIds: string[];
}

export interface V2Menu {
  readonly _id: string;
  readonly name: string;
  readonly itemCount: number;
  readonly categories: V2MenuCategory[];
}

/** Selection rule of a modifier group (FOYR-565). */
export interface V2ModifierRule {
  readonly required: boolean;
  readonly min: number;
  /** `null` means no limit (a Toast sync stores it, FOYR-642 D3). */
  readonly max: number | null;
}

export interface V2ModifierOption {
  readonly _id: string;
  readonly name: string;
  /** Added to the item price per unit; 0 shows as "—". */
  readonly priceCents: number;
}

export interface V2ModifierGroup {
  readonly _id: string;
  readonly name: string;
  readonly rule: V2ModifierRule;
  readonly options: V2ModifierOption[];
}

/** Restaurant branding (FOYR-564). Both values are `#RRGGBB` hex. */
export interface V2Branding {
  readonly primaryHex: string;
  readonly onPrimaryHex: string;
}

/** The reservation's stored order, as far as V2 reads it. */
export interface V2StoredOrder {
  readonly items: unknown[];
  /** When the order was placed (FOYR-566). */
  readonly placedAt?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim() !== "";

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const toCategory = (raw: unknown): V2MenuCategory | null => {
  if (!isRecord(raw) || !isNonEmptyString(raw._id)) return null;
  const itemIds = Array.isArray(raw.itemIds)
    ? raw.itemIds.filter(isNonEmptyString)
    : [];
  return {
    _id: raw._id,
    name: typeof raw.name === "string" ? raw.name : "",
    itemIds,
  };
};

const toMenu = (raw: unknown): V2Menu | null => {
  if (!isRecord(raw) || !isNonEmptyString(raw._id)) return null;
  const categories = Array.isArray(raw.categories)
    ? raw.categories
        .map(toCategory)
        .filter((c): c is V2MenuCategory => c !== null)
    : [];
  return {
    _id: raw._id,
    name: typeof raw.name === "string" ? raw.name : "",
    itemCount: isFiniteNumber(raw.itemCount) ? raw.itemCount : 0,
    categories,
  };
};

/** `menus[]` from the restaurant payload; absent (old backend) is `[]`. */
export function parseMenus(raw: unknown): V2Menu[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(toMenu).filter((m): m is V2Menu => m !== null);
}

const toOption = (raw: unknown): V2ModifierOption | null => {
  if (!isRecord(raw) || !isNonEmptyString(raw._id)) return null;
  return {
    _id: raw._id,
    name: typeof raw.name === "string" ? raw.name : "",
    priceCents:
      isFiniteNumber(raw.priceCents) && raw.priceCents > 0
        ? Math.round(raw.priceCents)
        : 0,
  };
};

const toMax = (raw: unknown, min: number, optionCount: number): number | null => {
  if (raw === null) return null;
  return Math.max(isFiniteNumber(raw) && raw > 0 ? raw : optionCount, min);
};

const toRule = (raw: unknown, optionCount: number): V2ModifierRule => {
  const record = isRecord(raw) ? raw : {};
  const min = isFiniteNumber(record.min) && record.min > 0 ? record.min : 0;
  return {
    required: record.required === true || min > 0,
    min,
    max: toMax(record.max, min, optionCount),
  };
};

/**
 * A served modifier group (FOYR-565). The legacy item shape put
 * `{ _modification, required, ... }` references in the same `modifiers`
 * array; those carry no `options` and are not V2 groups.
 */
const toModifierGroup = (raw: unknown): V2ModifierGroup | null => {
  if (!isRecord(raw) || !isNonEmptyString(raw._id)) return null;
  if (!Array.isArray(raw.options)) return null;
  const options = raw.options
    .map(toOption)
    .filter((o): o is V2ModifierOption => o !== null);
  const rule = toRule(raw.rule, options.length);
  // A required group with nothing servable stays (FOYR-669): the backend
  // enforces its rule, so a stored line must be judged by it here too.
  if (options.length === 0 && !rule.required) return null;
  return {
    _id: raw._id,
    name: typeof raw.name === "string" ? raw.name : "",
    rule,
    options,
  };
};

/** How many options a group needs before the item can be added. */
export const requiredCount = (group: V2ModifierGroup): number =>
  group.rule.required ? Math.max(1, group.rule.min) : group.rule.min;

/** How many picks a rule allows: `max: null` means no limit. */
export const pickLimit = (rule: V2ModifierRule): number =>
  rule.max ?? Infinity;

/** An item's `modifiers`; today the backend always sends `[]`. */
export function parseModifierGroups(raw: unknown): V2ModifierGroup[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(toModifierGroup)
    .filter((g): g is V2ModifierGroup => g !== null);
}

/**
 * `unavailableItemIds` / `unavailableOptionIds` from the restaurant payload
 * (FOYR-669). Undefined when absent: an old backend has not said, which is
 * not the same as `[]`.
 */
export function parseUnavailableIds(raw: unknown): string[] | undefined {
  return Array.isArray(raw) ? raw.filter(isNonEmptyString) : undefined;
}

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** `branding` from the restaurant payload, or null when absent or malformed. */
export function parseBranding(raw: unknown): V2Branding | null {
  if (!isRecord(raw)) return null;
  const { primaryHex, onPrimaryHex } = raw;
  if (typeof primaryHex !== "string" || !HEX_COLOR.test(primaryHex)) {
    return null;
  }
  return {
    primaryHex,
    onPrimaryHex:
      typeof onPrimaryHex === "string" && HEX_COLOR.test(onPrimaryHex)
        ? onPrimaryHex
        : "",
  };
}

/** The `phoneLast4` the reservation carries (FOYR-566), when it is four digits. */
export function parsePhoneLast4(raw: unknown): string | undefined {
  return typeof raw === "string" && /^\d{4}$/.test(raw) ? raw : undefined;
}

/** The reservation's stored order, typed; null when it holds no items. */
export function parseStoredOrder(raw: unknown): V2StoredOrder | null {
  if (!isRecord(raw) || !Array.isArray(raw.items) || raw.items.length === 0) {
    return null;
  }
  return {
    items: raw.items,
    placedAt: isNonEmptyString(raw.placedAt) ? raw.placedAt : undefined,
  };
}
